import { emptyDefinition, newId } from '../../../vendor/template-builder'

/**
 * "Yeni" derken açılan hazır onam formu şablonu.
 *
 * Amaç boş sayfayla başlamamak: tipik bir bilgilendirilmiş onam formunun iskeleti (başlık, hasta
 * bilgi tablosu, işlem listesi, bilgilendirme metni, onay kutuları, imza) hazır gelir; kullanıcı
 * üzerinde düzenler.
 *
 * Alan adları `OnamFormPlaceholders` katalogundakilerle birebir aynı olmalı — buraya katalogda
 * olmayan bir token yazılırsa builder "tanınmayan alan" uyarısı verir ve çıktıda boş kalır.
 */

/** Çok dilli metin: bilinen çeviriler doldurulur, kalan diller Türkçe metne düşer. */
const L = (texts, languages) =>
    languages.reduce((acc, lang) => ({ ...acc, [lang]: texts[lang] || texts.tr || '' }), {})

const cell = (texts, languages, extra = {}) => ({
    html: L(texts, languages),
    colSpan: 1,
    rowSpan: 1,
    ...extra,
})

const checkbox = (texts, languages) => ({
    id: newId(),
    type: 'checkbox',
    checked: false,
    key: '',
    label: L(texts, languages),
})

export function defaultConsentTemplate(languages, defaultLanguage) {
    const langs = languages && languages.length ? languages : ['tr']
    const base = emptyDefinition(langs, defaultLanguage || langs[0])

    return {
        ...base,
        name: '',
        header: {
            default: {
                blocks: [
                    {
                        id: newId(),
                        type: 'text',
                        align: 'center',
                        html: L(
                            { tr: '<p><strong>{{institutionName}}</strong></p>', en: '<p><strong>{{institutionName}}</strong></p>' },
                            langs
                        ),
                    },
                ],
            },
        },
        footer: {
            default: { blocks: [] },
            pageNumber: { enabled: true, format: '{PAGE} / {PAGES}', align: 'center' },
        },
        body: [
            {
                id: newId(),
                type: 'text',
                align: 'center',
                html: L(
                    {
                        tr: '<p><strong>BİLGİLENDİRİLMİŞ ONAM FORMU</strong></p>',
                        en: '<p><strong>INFORMED CONSENT FORM</strong></p>',
                    },
                    langs
                ),
            },

            {
                id: newId(),
                type: 'table',
                borders: true,
                headerBold: false,
                columns: [{ widthPercent: 50 }, { widthPercent: 50 }],
                rows: [
                    [
                        cell({ tr: 'Hasta Adı: {{patientName}} {{patientLastname}}', en: 'Patient: {{patientName}} {{patientLastname}}' }, langs),
                        cell({ tr: 'Randevu Tarihi: {{appointmentDate}}', en: 'Appointment: {{appointmentDate}}' }, langs),
                    ],
                    [
                        cell({ tr: 'Hasta Kimliği: {{patientId}}', en: 'Patient ID: {{patientId}}' }, langs),
                        cell({ tr: 'Kurum: {{institutionName}}', en: 'Institution: {{institutionName}}' }, langs),
                    ],
                    [
                        cell({ tr: 'Doğum Tarihi: {{patientBirthdate}}', en: 'Birthdate: {{patientBirthdate}}' }, langs),
                        cell({ tr: 'Cinsiyet: {{patientGender}}', en: 'Gender: {{patientGender}}' }, langs),
                    ],
                ],
            },

            { id: newId(), type: 'spacer', size: 1 },

            {
                id: newId(),
                type: 'text',
                align: 'left',
                html: L(
                    { tr: '<p><strong>Yapılacak İşlemler</strong></p>', en: '<p><strong>Procedures</strong></p>' },
                    langs
                ),
            },

            {
                id: newId(),
                type: 'repeat',
                key: '{{operations}}',
                borders: true,
                headerBold: true,
                columns: [
                    { key: '{{operationName}}', header: L({ tr: 'İşlem', en: 'Procedure' }, langs), widthPercent: 70 },
                    { key: '{{accessionNumber}}', header: L({ tr: 'Acc No', en: 'Acc No' }, langs), widthPercent: 30 },
                ],
            },

            { id: newId(), type: 'spacer', size: 1 },

            {
                id: newId(),
                type: 'text',
                align: 'justify',
                html: L(
                    {
                        tr:
                            '<p><strong>Bilgilendirme</strong></p>' +
                            '<p>Yukarıda belirtilen işlem(ler) hakkında bilgilendirildim. İşlemin amacı, ' +
                            'uygulanış şekli, olası yararları ve riskleri bana anlatıldı. Soru sorma fırsatı ' +
                            'verildi ve sorularım yanıtlandı.</p>',
                        en:
                            '<p><strong>Information</strong></p>' +
                            '<p>I have been informed about the procedure(s) listed above. The purpose, the way ' +
                            'it is performed, its possible benefits and risks were explained to me. I was given ' +
                            'the opportunity to ask questions and my questions were answered.</p>',
                    },
                    langs
                ),
            },

            // Koşullu blok: klinik bilgi girilmişse forma yazılır, boşsa hiç görünmez.
            {
                id: newId(),
                type: 'conditional',
                when: { key: '{{clinicalInformation}}', op: 'notEmpty', value: '' },
                blocks: [
                    {
                        id: newId(),
                        type: 'text',
                        align: 'left',
                        html: L(
                            {
                                tr: '<p><strong>Klinik Bilgi:</strong> {{clinicalInformation}}</p>',
                                en: '<p><strong>Clinical Information:</strong> {{clinicalInformation}}</p>',
                            },
                            langs
                        ),
                    },
                ],
            },

            { id: newId(), type: 'spacer', size: 1 },

            {
                id: newId(),
                type: 'text',
                align: 'left',
                html: L(
                    { tr: '<p><strong>Lütfen İşaretleyiniz</strong></p>', en: '<p><strong>Please Check</strong></p>' },
                    langs
                ),
            },

            checkbox({ tr: 'Bilgilendirmeyi okudum ve anladım.', en: 'I have read and understood the information.' }, langs),
            checkbox({ tr: 'İşlemin risklerini kabul ediyorum.', en: 'I accept the risks of the procedure.' }, langs),
            checkbox({ tr: 'Bilinen bir alerjim yok.', en: 'I have no known allergies.' }, langs),

            { id: newId(), type: 'spacer', size: 2 },

            {
                id: newId(),
                type: 'signature',
                slots: [
                    {
                        title: L({ tr: 'Hasta / Yasal Temsilci', en: 'Patient / Legal Representative' }, langs),
                        nameKey: '{{patientName}}',
                        signKey: '',
                        dateKey: '{{appointmentDate}}',
                    },
                    {
                        title: L({ tr: 'İşlemi Gerçekleştiren', en: 'Performed By' }, langs),
                        nameKey: '',
                        signKey: '',
                        dateKey: '',
                    },
                ],
            },
        ],
    }
}

export default defaultConsentTemplate
